import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Menu, X, User, LogOut, ShoppingBag } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { totalItems, setIsOpen } = useCart();
  const location = useLocation();

  const navLinks = [
    { name: 'Exhibitions', path: '/gallery' },
    { name: 'Shop', path: '/shop' },
    { name: 'Auction', path: '/auction' },
    { name: 'About', path: '/about' },
    { name: 'News', path: '/news' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Left: Explore text */}
          <div className="hidden md:flex items-center gap-8">
            <span className="text-[#A3A3A3] text-xs tracking-widest uppercase font-mono">Explore</span>
            <Link
              to="/gallery"
              className="text-[#C5A059] text-sm hover:text-[#E0BE77] transition-colors"
              data-testid="nav-latest"
            >
              Latest
            </Link>
          </div>

          {/* Center: Logo */}
          <Link 
            to="/" 
            className="font-serif text-xl md:text-2xl tracking-tight text-[#F5F5F0] hover:text-[#C5A059] transition-colors absolute left-1/2 -translate-x-1/2"
            data-testid="nav-logo"
          >
            Phaeton Imagery
          </Link>

          {/* Right: Cart + Auth */}
          <div className="hidden md:flex items-center gap-5">
            {/* Cart */}
            <button
              onClick={() => setIsOpen(true)}
              className="relative text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors"
              data-testid="nav-cart"
            >
              <ShoppingBag size={18} strokeWidth={1.5} />
              {totalItems > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#C5A059] text-[#0A0A0A] text-[9px] font-mono rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors"
                    data-testid="nav-user-menu"
                  >
                    <User size={16} strokeWidth={1.5} />
                    <span className="text-xs tracking-widest uppercase font-mono">
                      {user.name}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="bg-[#121212] border border-white/10 text-[#F5F5F0]"
                >
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer text-xs uppercase tracking-wider" data-testid="nav-admin">
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={logout}
                    className="cursor-pointer text-xs uppercase tracking-wider text-[#D84B79]"
                    data-testid="nav-logout"
                  >
                    <LogOut size={14} className="mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className="text-[#A3A3A3] hover:text-[#C5A059] text-xs tracking-widest uppercase font-mono transition-colors"
                data-testid="nav-login"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-[#F5F5F0]"
            data-testid="nav-mobile-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Secondary nav row */}
      <div className="hidden md:block border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-8 h-12 overflow-x-auto">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs tracking-widest uppercase font-mono whitespace-nowrap transition-colors ${
                  isActive(link.path)
                    ? 'text-[#C5A059]'
                    : 'text-[#A3A3A3] hover:text-[#F5F5F0]'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0A0A0A] border-t border-white/10">
          <div className="px-6 py-8 space-y-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block text-sm uppercase tracking-[0.2em] ${
                  isActive(link.path) ? 'text-[#C5A059]' : 'text-[#A3A3A3]'
                }`}
                data-testid={`nav-mobile-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block text-sm uppercase tracking-[0.2em] text-[#A3A3A3]" data-testid="nav-mobile-admin">Admin</Link>
                )}
                <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block text-sm uppercase tracking-[0.2em] text-[#D84B79]" data-testid="nav-mobile-logout">Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block text-sm uppercase tracking-[0.2em] text-[#C5A059]" data-testid="nav-mobile-login">Sign In</Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
